import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/api';
import { getStoredReferralCode, clearStoredReferralCode } from '../hooks/useReferralTracking';
import LanguageSelector from './LanguageSelector';
import './Onboarding.css';

function Onboarding() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0); // Start at 0 for language selection
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    goals: [],
    challenges: [],
    preferredTime: 'morgen',
  });
  const [inviteCode, setInviteCode] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check for invite code in URL
    const invite = searchParams.get('invite');
    if (invite) {
      setInviteCode(invite);
    }
    
    // Priority order for referral code:
    // 1. URL parameter (?ref=)
    // 2. LocalStorage (from useReferralTracking hook)
    // 3. Cookie (legacy fallback)
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      // Track click
      apiClient.trackAffiliateClick(ref).catch(() => {});
      
      // Set cookie (30 days) - legacy support
      document.cookie = `zenta_ref=${ref}; max-age=${30 * 24 * 60 * 60}; path=/`;
    } else {
      // Check localStorage first (from useReferralTracking)
      const storedRef = getStoredReferralCode();
      if (storedRef) {
        setReferralCode(storedRef);
      } else {
        // Fallback: Check if referral cookie exists
        const cookies = document.cookie.split(';');
        const refCookie = cookies.find(c => c.trim().startsWith('zenta_ref='));
        if (refCookie) {
          setReferralCode(refCookie.split('=')[1]);
        }
      }
    }
  }, [searchParams]);

  // Goals with translations
  const goals = [
    { id: 'better-sleep', labelKey: 'onboarding.goals.options.sleep', icon: '😴', frequencies: ['ZENTA.sleep', 'ZENTA.calm'], programs: ['Abend-Entspannung'] },
    { id: 'more-energy', labelKey: 'onboarding.goals.options.energy', icon: '⚡', frequencies: ['ZENTA.energy', 'ZENTA.focus'], programs: ['Morgen-Energie', 'Fokus-Flow'] },
    { id: 'reduce-stress', labelKey: 'onboarding.goals.options.stress', icon: '🧘', frequencies: ['ZENTA.calm', 'ZENTA.align'], programs: ['Abend-Entspannung'] },
    { id: 'increase-focus', labelKey: 'onboarding.goals.options.focus', icon: '🎯', frequencies: ['ZENTA.focus', 'ZENTA.align'], programs: ['Fokus-Flow', 'Morgen-Energie'] },
    { id: 'emotional-healing', labelKey: 'onboarding.goals.options.healing', icon: '💖', frequencies: ['ZENTA.heart', 'ZENTA.calm'], programs: ['Tiefen-Heilung'] },
    { id: 'spiritual-growth', labelKey: 'onboarding.goals.options.spiritual', icon: '🧘', frequencies: ['ZENTA.intuit', 'ZENTA.moon'], programs: ['Tiefen-Heilung'] },
  ];

  // Challenges with translations
  const challenges = [
    { id: 'sleep-problems', labelKey: 'onboarding.challenges.options.insomnia', icon: '🌙', frequencies: ['ZENTA.sleep', 'ZENTA.calm'] },
    { id: 'lack-energy', labelKey: 'onboarding.challenges.options.fatigue', icon: '🔋', frequencies: ['ZENTA.energy', 'ZENTA.body'] },
    { id: 'anxiety', labelKey: 'onboarding.challenges.options.anxiety', icon: '🌪️', frequencies: ['ZENTA.calm', 'ZENTA.protect'] },
    { id: 'concentration', labelKey: 'onboarding.challenges.options.concentration', icon: '🧠', frequencies: ['ZENTA.focus', 'ZENTA.align'] },
    { id: 'emotional-blockage', labelKey: 'onboarding.challenges.options.mood', icon: '🚫', frequencies: ['ZENTA.heart', 'ZENTA.calm'] },
    { id: 'disconnection', labelKey: 'onboarding.challenges.options.disconnect', icon: '🧩', frequencies: ['ZENTA.align', 'ZENTA.intuit'] },
  ];

  const handleToggleSelection = (field, value) => {
    const current = formData[field];
    const newValue = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setFormData({ ...formData, [field]: newValue });
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponValid(null);
      return;
    }

    try {
      const result = await apiClient.request(`/api/affiliate/validate-coupon/${couponCode.toUpperCase()}`);
      setCouponValid(result);
    } catch (error) {
      setCouponValid({ valid: false, message: i18n.language === 'de' ? 'Code ungültig' : 'Invalid code' });
    }
  };

  const handleLanguageSelect = () => {
    // After language selection, move to step 1
    setCurrentStep(1);
  };

  const handleNext = () => {
    setError('');

    if (currentStep === 1) {
      if (!formData.name.trim() || formData.name.length < 2) {
        setError(t('auth.register.error'));
        return;
      }
      if (!formData.email || !formData.email.includes('@')) {
        setError(t('auth.register.error'));
        return;
      }
      if (formData.password.length < 6) {
        setError(t('auth.register.error'));
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError(t('auth.register.error'));
        return;
      }
    }

    if (currentStep === 2 && formData.goals.length === 0) {
      setError(i18n.language === 'de' ? 'Bitte wähle mindestens ein Ziel aus' : 'Please select at least one goal');
      return;
    }

    if (currentStep === 3 && formData.challenges.length === 0) {
      setError(i18n.language === 'de' ? 'Bitte wähle mindestens eine Herausforderung aus' : 'Please select at least one challenge');
      return;
    }

    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      // Register the user (with or without invite code, referral, coupon)
      const registerData = inviteCode 
        ? await apiClient.registerWithInvite(formData.name, formData.email, formData.password, inviteCode, referralCode, couponCode)
        : await apiClient.registerWithName(formData.name, formData.email, formData.password, referralCode, couponCode);

      // Save language preference
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/language`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ language: i18n.language })
        });
      } catch (e) {
        console.log('Failed to save language preference');
      }

      // Clear stored referral code after successful registration
      clearStoredReferralCode();
      // Also clear cookie
      document.cookie = 'zenta_ref=; max-age=0; path=/';

      // If this is a sub-account (invited), skip onboarding and go straight to dashboard
      if (inviteCode) {
        navigate('/dashboard');
        return;
      }

      // Get all frequencies and programs
      let frequencies = [];
      let programs = [];
      
      try {
        const [freqResponse, progResponse] = await Promise.all([
          apiClient.getFrequencies(),
          apiClient.getPrograms(),
        ]);
        
        // Ensure we have arrays - handle both direct arrays and wrapped responses
        frequencies = Array.isArray(freqResponse) ? freqResponse : (freqResponse?.data || freqResponse?.frequencies || []);
        programs = Array.isArray(progResponse) ? progResponse : (progResponse?.data || progResponse?.programs || []);
      } catch (apiError) {
        console.warn('Could not fetch frequencies/programs for recommendations:', apiError);
        // Continue without recommendations - user can still complete onboarding
      }

      // Calculate recommendations
      const recommendedFreqNames = new Set();
      const recommendedProgNames = new Set();

      // Based on goals
      formData.goals.forEach(goalId => {
        const goal = goals.find(g => g.id === goalId);
        if (goal) {
          goal.frequencies.forEach(f => recommendedFreqNames.add(f));
          goal.programs.forEach(p => recommendedProgNames.add(p));
        }
      });

      // Based on challenges
      formData.challenges.forEach(challengeId => {
        const challenge = challenges.find(c => c.id === challengeId);
        if (challenge) {
          challenge.frequencies.forEach(f => recommendedFreqNames.add(f));
        }
      });

      // Convert to IDs - ensure arrays before filtering
      const recommendedFrequencies = Array.isArray(frequencies) 
        ? frequencies.filter(f => f && recommendedFreqNames.has(f.name)).map(f => f.id)
        : [];

      const recommendedPrograms = Array.isArray(programs)
        ? programs.filter(p => p && recommendedProgNames.has(p.name)).map(p => p.id)
        : [];

      // Save onboarding data
      await apiClient.completeOnboarding({
        goals: formData.goals,
        challenges: formData.challenges,
        preferred_time: formData.preferredTime,
        recommended_frequencies: recommendedFrequencies,
        recommended_programs: recommendedPrograms,
      });

      // Auto-favorite recommended frequencies
      for (const freqId of recommendedFrequencies.slice(0, 3)) {
        try {
          await apiClient.addToFavorites(freqId);
        } catch (e) {
          console.log('Already favorited or error:', e);
        }
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 5; // Language + 4 original steps
  const progressPercent = (currentStep / (totalSteps - 1)) * 100;

  return (
    <div className="onboarding-container">
      {/* Skip progress bar on language selection */}
      {currentStep > 0 && (
        <div className="onboarding-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>
          <div className="progress-text">
            {t('onboarding.progress', { current: currentStep, total: totalSteps - 1 })}
          </div>
        </div>
      )}

      <div className="onboarding-content">
        {/* Step 0: Language Selection */}
        {currentStep === 0 && (
          <div className="onboarding-step language-step">
            <LanguageSelector onComplete={handleLanguageSelect} />
          </div>
        )}

        {/* Step 1: Account erstellen */}
        {currentStep === 1 && (
          <div className="onboarding-step">
            <div className="step-header">
              {inviteCode ? (
                <>
                  <h1 className="step-title">🎉 {i18n.language === 'de' ? 'Du wurdest eingeladen!' : 'You\'ve been invited!'}</h1>
                  <p className="step-subtitle">{i18n.language === 'de' ? 'Erstelle deinen Account und erhalte sofort Premium-Zugriff' : 'Create your account and get instant Premium access'}</p>
                </>
              ) : (
                <>
                  <h1 className="step-title">👋 {t('onboarding.welcome.title')}</h1>
                  <p className="step-subtitle">{t('onboarding.welcome.subtitle')}</p>
                </>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label>{t('auth.register.name')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('auth.register.namePlaceholder')}
                autoFocus
                data-testid="onboarding-name-input"
              />
            </div>

            <div className="form-group">
              <label>{t('auth.register.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('auth.register.emailPlaceholder')}
                data-testid="onboarding-email-input"
              />
            </div>

            <div className="form-group">
              <label>{t('auth.register.password')}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={t('auth.register.passwordPlaceholder')}
                data-testid="onboarding-password-input"
              />
            </div>

            <div className="form-group">
              <label>{t('auth.register.confirmPassword')}</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder={i18n.language === 'de' ? 'Passwort wiederholen' : 'Repeat password'}
                data-testid="onboarding-confirm-password-input"
              />
            </div>

            {referralCode && (
              <div className="referral-notice">
                <span className="referral-icon">🎁</span>
                <span>{i18n.language === 'de' ? 'Du wurdest empfohlen von:' : 'Referred by:'} <strong>{referralCode}</strong></span>
              </div>
            )}

            {!inviteCode && (
              <div className="form-group">
                <label>{t('checkout.couponCode')}</label>
                <div className="coupon-input-group">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponValid(null);
                    }}
                    onBlur={handleValidateCoupon}
                    placeholder={t('checkout.couponPlaceholder')}
                    style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
                    data-testid="onboarding-coupon-input"
                  />
                  {couponValid && (
                    <div className={`coupon-validation ${couponValid.valid ? 'valid' : 'invalid'}`}>
                      {couponValid.message}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Ziele */}
        {currentStep === 2 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h1 className="step-title">🎯 {t('onboarding.goals.title')}</h1>
              <p className="step-subtitle">{t('onboarding.goals.subtitle')}</p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="selection-grid">
              {goals.map((goal) => (
                <button
                  key={goal.id}
                  className={`selection-card ${formData.goals.includes(goal.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleSelection('goals', goal.id)}
                  data-testid={`goal-${goal.id}`}
                >
                  <div className="selection-icon">{goal.icon}</div>
                  <div className="selection-label">{t(goal.labelKey)}</div>
                  {formData.goals.includes(goal.id) && (
                    <div className="selection-check">✓</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Herausforderungen */}
        {currentStep === 3 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h1 className="step-title">🧘 {t('onboarding.challenges.title')}</h1>
              <p className="step-subtitle">{t('onboarding.challenges.subtitle')}</p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="selection-grid">
              {challenges.map((challenge) => (
                <button
                  key={challenge.id}
                  className={`selection-card ${formData.challenges.includes(challenge.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleSelection('challenges', challenge.id)}
                  data-testid={`challenge-${challenge.id}`}
                >
                  <div className="selection-icon">{challenge.icon}</div>
                  <div className="selection-label">{t(challenge.labelKey)}</div>
                  {formData.challenges.includes(challenge.id) && (
                    <div className="selection-check">✓</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Tageszeit & Zusammenfassung */}
        {currentStep === 4 && (
          <div className="onboarding-step">
            <div className="step-header">
              <h1 className="step-title">⏰ {t('onboarding.time.title')}</h1>
              <p className="step-subtitle">{t('onboarding.time.subtitle')}</p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="time-selection">
              <button
                className={`time-card ${formData.preferredTime === 'morgen' ? 'selected' : ''}`}
                onClick={() => setFormData({ ...formData, preferredTime: 'morgen' })}
                data-testid="time-morning"
              >
                <div className="time-icon">☀️</div>
                <div className="time-label">{t('onboarding.time.options.morning')}</div>
                <p>{i18n.language === 'de' ? 'Energie & Fokus für den Tag' : 'Energy & focus for the day'}</p>
              </button>
              <button
                className={`time-card ${formData.preferredTime === 'abend' ? 'selected' : ''}`}
                onClick={() => setFormData({ ...formData, preferredTime: 'abend' })}
                data-testid="time-evening"
              >
                <div className="time-icon">🌙</div>
                <div className="time-label">{t('onboarding.time.options.evening')}</div>
                <p>{i18n.language === 'de' ? 'Entspannung & Regeneration' : 'Relaxation & regeneration'}</p>
              </button>
              <button
                className={`time-card ${formData.preferredTime === 'beides' ? 'selected' : ''}`}
                onClick={() => setFormData({ ...formData, preferredTime: 'beides' })}
                data-testid="time-anytime"
              >
                <div className="time-icon">🌞</div>
                <div className="time-label">{t('onboarding.time.options.anytime')}</div>
                <p>{i18n.language === 'de' ? 'Ganztägige Unterstützung' : 'All-day support'}</p>
              </button>
            </div>

            <div className="summary-box">
              <h3>✨ {t('onboarding.recommendations.title')}</h3>
              <p>{t('onboarding.recommendations.subtitle')}</p>
              <ul>
                <li>✅ {i18n.language === 'de' ? 'Personalisierte Frequenz-Auswahl' : 'Personalized frequency selection'}</li>
                <li>✅ {i18n.language === 'de' ? 'Maßgeschneiderte Programme' : 'Tailored programs'}</li>
                <li>✅ {i18n.language === 'de' ? 'Optimale Tagesroutinen' : 'Optimal daily routines'}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="onboarding-actions">
          {currentStep > 1 && (
            <button onClick={handleBack} className="btn-back" data-testid="onboarding-back-btn">
              ← {t('common.back')}
            </button>
          )}
          {currentStep > 0 && currentStep < 4 ? (
            <button onClick={handleNext} className="btn-next" data-testid="onboarding-next-btn">
              {t('common.next')} →
            </button>
          ) : currentStep === 4 ? (
            <button onClick={handleComplete} className="btn-complete" disabled={loading} data-testid="onboarding-complete-btn">
              {loading ? t('common.loading') : `✨ ${i18n.language === 'de' ? 'ZENTA erleben' : 'Experience ZENTA'}`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
